import type { DirectiveOptions } from 'vue';
import { isInstanceOfSafeHtml } from '@mimir/common/util/safeHtmlBuilder';
import V3Tooltip from '@mimir/web/compat/V3Tooltip.vue';
import type { DirectiveBinding } from 'vue/types/options';
import Vue from 'vue';
import type { CombinedVueInstance } from 'vue/types/vue';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import debounce from 'lodash/debounce';

function createDiv(height: number) {
  const div = document.createElement('div');
  div.style.top = '0px';
  div.style.right = '0px';
  div.style.width = '7px';
  div.style.position = 'absolute';
  div.style.cursor = 'col-resize';
  div.style.userSelect = 'none';
  div.style.height = '100%';
  div.classList.add('drag-line');
  return div;
}

function getStyleVal(elm: HTMLElement, css: string) {
  return (window.getComputedStyle(elm, null).getPropertyValue(css));
}

export const resizableColumnsDirective: DirectiveOptions = {
  inserted: (el, bindings) => {
    const bindValues: { skipEdges: boolean, minWidth: number, resized?: () => void } = bindings.value;
    const { skipEdges, minWidth, resized } = bindValues;

    const table = el.querySelector('table');
    if (!table) {
      console.error('v-resizable-columns only valid for TABLE elements!');
      return;
    }

    // CSS in main.style
    table.classList.add('resizable-table');
    const row = table.getElementsByTagName('tr')[0];
    const cols = row ? row.children : undefined;
    if (!cols) {
      return;
    }

    setTimeout(() => {
      // Here setTimeout ensures when inserted hook executes we
      // get the accurate table offsetHeight height.
      const tableHeight = table.offsetHeight;
      const start = skipEdges ? 1 : 0;
      for (let i = start; i < cols.length - 1; i++) {
        const div = createDiv(tableHeight);
        const col = cols[i] as HTMLTableColElement;
        col.appendChild(div);
        col.style.position = 'sticky';
        setListeners(div);
      }
    }, 1);

    function setListeners(div: HTMLDivElement) {
      let pageX: number | undefined;
      let curCol: HTMLTableColElement | undefined;
      let nxtCol: HTMLTableColElement | undefined;
      let curColWidth: number | undefined;
      let nxtColWidth: number | undefined;

      div.addEventListener('mousedown', e => {
        const targetDiv = e.target as HTMLDivElement;
        curCol = targetDiv.parentElement as HTMLTableColElement;
        nxtCol = curCol.nextElementSibling as HTMLTableColElement | undefined;
        pageX = e.pageX;

        const padding = paddingDiff(curCol);

        curColWidth = curCol.offsetWidth - padding;
        if (nxtCol)
          nxtColWidth = nxtCol.offsetWidth - padding;

        const onMouseUp = (ev: MouseEvent) => {
          if (resized)
            resized();
          document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mouseup', onMouseUp);
      });

      div.addEventListener('mouseover', e => {
        const target = e.target as HTMLDivElement | null;
        if (target)
          target.classList.add('drag-hovered');
      });

      div.addEventListener('mouseout', e => {
        const target = e.target as HTMLDivElement | null;
        if (target)
          target.classList.remove('drag-hovered');
      });

      document.addEventListener('mousemove', e => {
        if (curCol && pageX !== undefined) {
          const diffX = e.pageX - pageX;
          let curColLimited = false;
          let nxtColLimited = false;
          let curColNewWidth: number = -1;
          let nxtColNewWidth: number = -1;
          if (curCol && curColWidth !== undefined) {
            curColNewWidth = curColWidth + diffX;
            if (curColNewWidth <= minWidth) {
              curColNewWidth = minWidth;
              curColLimited = true;
            }
          }
          if (nxtCol && nxtColWidth !== undefined && !curColLimited) {
            nxtColNewWidth = nxtColWidth - diffX;
            if (nxtColNewWidth <= minWidth) {
              nxtColNewWidth = minWidth;
              nxtColLimited = true;
            }
          }

          if (nxtColNewWidth > 0 && !curColLimited) {
            nxtCol!.style.width = `${nxtColNewWidth}px`;

            const headerDiv = nxtCol!.querySelector('.th-header') as HTMLDivElement;
            if (headerDiv)
              headerDiv.style.width = `${Math.max(nxtColNewWidth - 6, 0)}px`;
          }

          if (curColNewWidth > 0 && !nxtColLimited) {
            curCol.style.width = `${curColNewWidth}px`;

            const headerDiv = curCol.querySelector('.th-header') as HTMLDivElement;
            if (headerDiv)
              headerDiv.style.width = `${Math.max(curColNewWidth - 6, 0)}px`;
          }
        }
      });

      document.addEventListener('mouseup', e => {
        curCol = undefined;
        nxtCol = undefined;
        pageX = undefined;
        nxtColWidth = undefined;
        curColWidth = undefined;
      });
    }

    function paddingDiff(col: HTMLTableColElement) {
      if (getStyleVal(col, 'box-sizing') == 'border-box') {
        return 0;
      }

      const padLeft = getStyleVal(col, 'padding-left');
      const padRight = getStyleVal(col, 'padding-right');

      return (parseInt(padLeft) + parseInt(padRight));
    }
  },
};

export const safeHtmlDirective: DirectiveOptions = {
  bind: (el, binding) => {
    renderSafeHtml(el, binding.value);
  },
  update: (el, binding) => {
    renderSafeHtml(el, binding.value);
  },
};

const renderSafeHtml = (el: HTMLElement, builder: any) => {
  if (!isInstanceOfSafeHtml(builder)) {
    throw new Error('Invalid type provided for v-safe-html. Expected an object of SafeHtmlBuilder instance.');
  }

  el.innerHTML = builder.getSafeHtml();
};

const observers = new WeakMap<HTMLElement, ResizeObserver>();
export const resizeObserverDirective: DirectiveOptions = {
  bind: (el, binding) => {
    const callback = binding.value;
    if (!(callback instanceof Function)) throw `v-onresize received ${typeof callback} ${binding.expression} instead of callback`;
    const resizeObserver = new ResizeObserver(debounce(entries => callback(entries[0].contentRect.width, entries[0].contentRect.height)));
    resizeObserver.observe(el);
    observers.set(el, resizeObserver);
  },
  unbind: (el) => {
    observers.get(el)?.disconnect();
  }
};


type VueInstance = CombinedVueInstance<any, any, any, any, any>;
const tooltips = new WeakMap<HTMLElement, VueInstance>();

export interface TooltipDirectiveConfig {
  disabled?: boolean,
  nudgeRight?: number,
  openDelay?: number,
  text: string,
}

function getTooltipDirectiveConfig(binding: DirectiveBinding): TooltipDirectiveConfig {
  if (isObject(binding.value)) {
    const maybe = binding.value as Partial<TooltipDirectiveConfig>;
    return {
      text: typeof maybe.text === 'string' ? maybe.text : '',
      disabled: Boolean(maybe.disabled),
      nudgeRight: typeof maybe.nudgeRight === 'number' ? maybe.nudgeRight : undefined,
      openDelay: typeof maybe.openDelay === 'number' ? maybe.openDelay : undefined,
    };
  }

  return {
    text: typeof binding.value === 'string' ? binding.value : '',
    disabled: false,
  };
}

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

function createTooltip(el: HTMLElement, binding: DirectiveBinding): VueInstance {
  const tdc = getTooltipDirectiveConfig(binding);
  const position: TooltipPosition | undefined = binding.arg as TooltipPosition ?? 'bottom';

  const Tooltip = Vue.extend(V3Tooltip);
  const instance = new Tooltip({
    propsData: {
      position,
      ...tdc,
      activator: el
    }
  });
  instance.$mount();

  tooltips.set(el, instance);
  return instance;
}

export const tooltipDirective: DirectiveOptions = {
  bind(el, bindings) {
    createTooltip(el, bindings);
  },
  update(el, binding) {
    const tdc = getTooltipDirectiveConfig(binding);
    const instance = tooltips.get(el);

    const wasDisabled = !instance.text || instance.disabled;
    const willHaveText = Boolean(tdc.text);

    instance.text = tdc.text;
    instance.disabled = tdc.disabled;

    // If tooltips are initially empty, undefined or disabled,
    // then reinitialize the tooltip to ensure it is displayed correctly.
    if (wasDisabled && willHaveText) {
      instance.$destroy();
      tooltips.delete(el);
      createTooltip(el, binding);
    }
  },
  unbind(el) {
    tooltips.get(el)?.$destroy();
    tooltips.delete(el);
  }
};
